import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { FileText, CheckCircle, XCircle, Clock, Eye, Filter, Loader } from 'lucide-react';
import PageHeader from '../components/admin/PageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPE_LABELS = {
  identity: { tr: 'Kimlik', en: 'Identity' },
  diploma: { tr: 'Diploma', en: 'Diploma' },
  photo: { tr: 'Fotoğraf', en: 'Photo' },
  certificate: { tr: 'Sertifika', en: 'Certificate' },
  contract: { tr: 'Sözleşme', en: 'Contract' },
  other: { tr: 'Diğer', en: 'Other' },
};

function StatusBadge({ status, lang }) {
  const t = (tr, en) => lang === 'tr' ? tr : en;
  const config = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label_tr: 'Bekliyor', label_en: 'Pending' },
    approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label_tr: 'Onaylı', label_en: 'Approved' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label_tr: 'Reddedildi', label_en: 'Rejected' },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${c.bg} ${c.text}`}>
      <Icon size={12} />
      {t(c.label_tr, c.label_en)}
    </span>
  );
}

export default function AdminDocuments() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [processing, setProcessing] = useState(null);

  const t = (tr, en) => lang === 'tr' ? tr : en;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get('user_id');
    if (uid) setUserFilter(uid);
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (userFilter) params.append('user_id', userFilter);
      params.append('limit', '100');

      const res = await axios.get(`${API}/admin/documents?${params.toString()}`, { headers: authHeaders() });
      setDocuments(res.data.documents || []);
      setStats(res.data.stats || { all: 0, pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocuments(); }, [statusFilter, typeFilter, userFilter]); // eslint-disable-line

  const handlePreview = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/preview`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('Onizleme acilamadi', 'Preview failed'));
    }
  };

  const handleApprove = async (docId) => {
    if (!window.confirm(t('Bu belgeyi onaylamak istediğinize emin misiniz?', 'Approve this document?'))) return;
    setProcessing(docId);
    try {
      await axios.post(`${API}/admin/documents/${docId}/approve`, {}, { headers: authHeaders() });
      toast.success(t('Belge onaylandı', 'Document approved'));
      await fetchDocuments();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('Onay başarısız', 'Approve failed'));
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (docId) => {
    const reason = window.prompt(t('Red sebebini giriniz:', 'Enter rejection reason:'));
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.warning(t('Sebep boş bırakılamaz', 'Reason required'));
      return;
    }
    setProcessing(docId);
    try {
      await axios.post(
        `${API}/admin/documents/${docId}/reject`,
        { reason: trimmed },
        { headers: authHeaders() }
      );
      toast.success(t('Belge reddedildi', 'Document rejected'));
      await fetchDocuments();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('Red başarısız', 'Reject failed'));
    } finally {
      setProcessing(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US') : '-';
  const fmtSize = (s) => s ? `${(s / 1024).toFixed(1)} KB` : '-';

  const statCards = [
    { key: 'all', label_tr: 'Toplam', label_en: 'Total', bg: 'bg-gray-50', valueColor: 'text-gray-700', labelColor: 'text-gray-600', value: stats.all },
    { key: 'pending', label_tr: 'Bekleyen', label_en: 'Pending', bg: 'bg-yellow-50', valueColor: 'text-yellow-700', labelColor: 'text-yellow-600', value: stats.pending },
    { key: 'approved', label_tr: 'Onaylı', label_en: 'Approved', bg: 'bg-green-50', valueColor: 'text-green-700', labelColor: 'text-green-600', value: stats.approved },
    { key: 'rejected', label_tr: 'Reddedilen', label_en: 'Rejected', bg: 'bg-red-50', valueColor: 'text-red-700', labelColor: 'text-red-600', value: stats.rejected },
  ];

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: lang === 'tr' ? 'Belgeler' : 'Documents' },
        ]}
        title={lang === 'tr' ? 'Belge Yönetimi' : 'Document Management'}
        subtitle={lang === 'tr' ? 'Öğrenci belgelerini incele, onayla veya reddet' : 'Review, approve or reject student documents'}
      />
      <div className="grid grid-cols-4 gap-4 mb-6">
        {statCards.map(s => (
          <div key={s.key} className={`card-clay p-4 text-center ${s.bg}`} data-testid={`stat-${s.key}`}>
            <div className={`text-3xl font-bold ${s.valueColor}`}>{s.value}</div>
            <div className={`text-sm font-bold ${s.labelColor}`}>{t(s.label_tr, s.label_en)}</div>
          </div>
        ))}
      </div>

      <div className="card-clay p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={16} />
          <span className="text-sm font-bold">{t('Filtre', 'Filter')}:</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            data-testid="status-filter"
          >
            <option value="">{t('Tüm durumlar', 'All status')}</option>
            <option value="pending">{t('Bekleyen', 'Pending')}</option>
            <option value="approved">{t('Onaylı', 'Approved')}</option>
            <option value="rejected">{t('Reddedilen', 'Rejected')}</option>
          </select>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            data-testid="type-filter"
          >
            <option value="">{t('Tüm tipler', 'All types')}</option>
            {Object.entries(DOC_TYPE_LABELS).map(([key, v]) => (
              <option key={key} value={key}>{t(v.tr, v.en)}</option>
            ))}
          </select>
          {userFilter && (
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono" data-testid="user-filter-chip">
              user_id: {userFilter}
              <button onClick={() => setUserFilter('')} className="ml-2 text-red-500 font-bold">&times;</button>
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader size={32} className="animate-spin mx-auto text-blue-500" />
        </div>
      ) : documents.length === 0 ? (
        <div className="card-clay p-12 text-center" data-testid="empty-state">
          <FileText size={48} className="mx-auto text-gray-400 mb-3" />
          <p className="font-bold text-gray-500">{t('Belge yok', 'No documents')}</p>
        </div>
      ) : (
        <div className="card-clay overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 font-bold">{t('Öğrenci', 'Student')}</th>
                <th className="text-left p-3 font-bold">{t('Tip', 'Type')}</th>
                <th className="text-left p-3 font-bold">{t('Başlık', 'Title')}</th>
                <th className="text-left p-3 font-bold">{t('Boyut', 'Size')}</th>
                <th className="text-left p-3 font-bold">{t('Tarih', 'Date')}</th>
                <th className="text-left p-3 font-bold">{t('Durum', 'Status')}</th>
                <th className="text-right p-3 font-bold">{t('İşlem', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.document_id} className="border-t hover:bg-gray-50" data-testid={`admin-doc-row-${doc.document_id}`}>
                  <td className="p-3">
                    <div className="font-bold">{doc.student_name}</div>
                    <div className="text-xs text-gray-500">{doc.student_email}</div>
                  </td>
                  <td className="p-3">{t(DOC_TYPE_LABELS[doc.document_type]?.tr, DOC_TYPE_LABELS[doc.document_type]?.en) || doc.document_type}</td>
                  <td className="p-3">
                    <div className="truncate max-w-[200px]">{doc.title}</div>
                    <div className="text-xs text-gray-400 truncate">{doc.original_filename}</div>
                  </td>
                  <td className="p-3 text-gray-600">{fmtSize(doc.size)}</td>
                  <td className="p-3 text-gray-600">{fmtDate(doc.uploaded_at)}</td>
                  <td className="p-3">
                    <StatusBadge status={doc.status} lang={lang} />
                    {doc.status === 'rejected' && doc.rejection_reason && (
                      <div className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={doc.rejection_reason}>
                        {doc.rejection_reason}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => handlePreview(doc.document_id)}
                        className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                        title={t('Onizle', 'Preview')}
                        data-testid={`preview-admin-${doc.document_id}`}
                      >
                        <Eye size={14} />
                      </button>
                      {doc.status !== 'approved' && (
                        <button
                          onClick={() => handleApprove(doc.document_id)}
                          disabled={processing === doc.document_id}
                          className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50"
                          title={t('Onayla', 'Approve')}
                          data-testid={`approve-${doc.document_id}`}
                        >
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {doc.status !== 'rejected' && (
                        <button
                          onClick={() => handleReject(doc.document_id)}
                          disabled={processing === doc.document_id}
                          className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                          title={t('Reddet', 'Reject')}
                          data-testid={`reject-${doc.document_id}`}
                        >
                          <XCircle size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
